import { Link, useRouterState } from '@tanstack/react-router'
import { StatusBar, type NodeStatus, type BudgetInfo } from './StatusBar'
import { useState, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { DashboardWS } from './DashboardWS.js'

const navItems = [
  { to: '/' as const, label: 'Board', icon: '▦' },
  { to: '/activity' as const, label: 'Activity', icon: '↻' },
  { to: '/chat' as const, label: 'Chat', icon: '◬' },
  { to: '/approvals' as const, label: 'Approvals', icon: '✓' },
  { to: '/crons' as const, label: 'Crons', icon: '⏱' },
  { to: '/settings' as const, label: 'Settings', icon: '⚙' },
]

export function Layout({ children }: { children: React.ReactNode }) {
  const router = useRouterState()
  const currentPath = router.location.pathname

  // Status bar state — updated via WebSocket health:update events
  const [nodes, setNodes] = useState<NodeStatus[]>([])
  const [activeSessions, setActiveSessions] = useState(0)
  const [budget, setBudget] = useState<BudgetInfo>({ used: 0, total: 100 })
  const queryClient = useQueryClient()

  useEffect(() => {
    const ws = new DashboardWS()

    const cleanup = ws.onEvent((msg) => {
      const type = msg.type as string

      // Update StatusBar state from health:update payload
      if (type === 'health:update' && msg.payload) {
        const p = msg.payload as { nodes?: NodeStatus[]; activeSessions?: number; budget?: BudgetInfo }
        if (p.nodes) setNodes(p.nodes)
        if (p.activeSessions != null) setActiveSessions(p.activeSessions)
        if (p.budget) setBudget(p.budget)
      }

      // On replay:gap, invalidate everything and refetch
      if (type === 'replay:gap') {
        queryClient.invalidateQueries()
        return
      }

      // Targeted invalidations by event type
      if (type.startsWith('approval:')) {
        queryClient.invalidateQueries({ queryKey: ['approvals'] })
      } else if (type.startsWith('card:')) {
        queryClient.invalidateQueries({ queryKey: ['cards'] })
      } else if (type.startsWith('session:')) {
        queryClient.invalidateQueries({ queryKey: ['sessions'] })
      } else if (type === 'health:update') {
        queryClient.invalidateQueries({ queryKey: ['health'] })
      } else if (type === 'node:status') {
        queryClient.invalidateQueries({ queryKey: ['nodes'] })
      }
    })

    ws.connect()

    return () => {
      cleanup()
      ws.destroy()
    }
  }, [queryClient])

  return (
    <div data-testid="app-layout" className="flex flex-col min-h-screen bg-gray-950 text-gray-100">
      <div className="flex flex-1 overflow-hidden">
        <aside
          data-testid="sidebar"
          className="flex flex-col w-56 bg-gray-900 border-r border-gray-800"
        >
          <div className="flex items-center gap-2 px-4 py-4 border-b border-gray-800">
            <span className="text-lg font-bold text-blue-400">Droid Instinct</span>
          </div>
          <nav className="flex-1 py-2" data-testid="nav">
            {navItems.map((item) => {
              const isActive = item.to === '/'
                ? currentPath === '/'
                : currentPath.startsWith(item.to)
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                    isActive
                      ? 'bg-gray-800 text-white border-r-2 border-blue-500'
                      : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800/50'
                  }`}
                >
                  <span className="text-base">{item.icon}</span>
                  {item.label}
                </Link>
              )
            })}
          </nav>
          <div className="px-4 py-3 border-t border-gray-800 text-xs text-gray-500">
            Droid Instinct v0.1
          </div>
        </aside>
        <main className="flex-1 overflow-auto" data-testid="main-content">
          {children}
        </main>
      </div>
      <StatusBar nodes={nodes} activeSessions={activeSessions} budget={budget} />
    </div>
  )
}
